import { useState } from "react";

import { IHospitalDetail } from "../../../../@types/admin/AdminHospitalDetails";
import {
  AuditLogPanel,
  FieldHint,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
  Tab,
  TabsContainer,
} from "../../shared";
import { TabStack } from "./HospitalDetails.styles";

/**
 * The audited tables behind the two trails (see the AuditLogPanel consumer
 * map). `cms_hospitalgroup` is the hospital record itself;
 * `HospitalConfigurations` is its feature configuration, audited separately.
 */
const HOSPITAL_TABLE = "cms_hospitalgroup";
const CONFIG_TABLE = "HospitalConfigurations";

type IHistoryTab = "hospital" | "config";

interface IHospitalHistoryTabProps {
  hospital: IHospitalDetail;
}

/**
 * History tab of the Hospital Details page (EN-5172).
 *
 * Retool had two "history" links on its hospital form, each opening
 * `moduleAuditLogs` in its own modal — one on `cms_hospitalgroup`, one on
 * `HospitalConfigurations`. The portal has a single global modal slot and the
 * panel's own row drill-down uses it, so they are tabs (the EN-5147 rule).
 *
 * The ward and ward-config trails are not here: they belong to a ward, and the
 * shared `WardDetailsPanel` on the Wards tab already carries them.
 *
 * Both trails read `/Retool/AuditLogs`, whose permission is `audit-logs:read`
 * (`AuditLogsAccessHandler`), not a page permission — so `hospitals:access`
 * alone is not enough for them.
 */
export function HospitalHistoryTab({ hospital }: IHospitalHistoryTabProps) {
  const [tab, setTab] = useState<IHistoryTab>("hospital");

  return (
    <TabStack>
      <SectionCard>
        <SectionHeader>
          <SectionHeading>
            <SectionTitle>History</SectionTitle>
          </SectionHeading>
        </SectionHeader>

        <TabsContainer>
          <Tab
            type="button"
            active={tab === "hospital"}
            onClick={() => setTab("hospital")}
          >
            Hospital record
          </Tab>
          <Tab
            type="button"
            active={tab === "config"}
            onClick={() => setTab("config")}
          >
            Configuration
          </Tab>
        </TabsContainer>

        {tab === "hospital" && (
          <AuditLogPanel
            key="hospital-history"
            tableName={HOSPITAL_TABLE}
            entityId={Number(hospital.id)}
            title="Hospital record changes"
          />
        )}

        {tab === "config" && (
          <>
            {!hospital.hospitalConfigurationId && (
              <FieldHint>
                This hospital has no configuration row yet, so there is nothing
                to show. One is created the first time its features are saved.
              </FieldHint>
            )}
            {hospital.hospitalConfigurationId && (
              <AuditLogPanel
                key="hospital-config-history"
                tableName={CONFIG_TABLE}
                // Keyed on the CONFIGURATION id, not the hospital's — Retool
                // read `getHospitalDetails.data.hospitalConfigurationId` here.
                entityId={hospital.hospitalConfigurationId}
                title="Configuration changes"
              />
            )}
          </>
        )}
      </SectionCard>
    </TabStack>
  );
}
